<template>
    <BaseModal
        :title="isEditing ? 'Edit Subject' : 'Add Subject'"
        :loading="loading"
        :submit-text="isEditing ? 'Update Subject' : 'Create Subject'"
        max-width="4xl"
        @close="emit('close')"
        @submit="handleSubmit"
    >
        <form @submit.prevent="handleSubmit">
            <div class="grid grid-cols-2 gap-8">
                <!-- Left Column -->
                <div class="space-y-6">
                    <!-- Code -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Code
                        </label>
                        <input
                            v-model="formData.code"
                            type="text"
                            maxlength="10"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                            placeholder="e.g., MATH, PHYS"
                            required
                        />
                        <div
                            v-if="errors.code"
                            class="mt-1 text-sm text-red-600 dark:text-red-400"
                        >
                            {{ errors.code[0] }}
                        </div>
                    </div>

                    <!-- Name -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Name
                        </label>
                        <input
                            v-model="formData.name"
                            type="text"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                            placeholder="e.g., Mathematics, Physics"
                            required
                        />
                        <div
                            v-if="errors.name"
                            class="mt-1 text-sm text-red-600 dark:text-red-400"
                        >
                            {{ errors.name[0] }}
                        </div>
                    </div>

                    <!-- Description -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Description
                        </label>
                        <textarea
                            v-model="formData.description"
                            rows="4"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                            placeholder="Optional subject description"
                        ></textarea>
                        <div
                            v-if="errors.description"
                            class="mt-1 text-sm text-red-600 dark:text-red-400"
                        >
                            {{ errors.description[0] }}
                        </div>
                    </div>

                    <!-- Credit Hours -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Credit Hours
                        </label>
                        <input
                            v-model.number="formData.credit_hours"
                            type="number"
                            min="1"
                            max="10"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                            required
                        />
                        <div
                            v-if="errors.credit_hours"
                            class="mt-1 text-sm text-red-600 dark:text-red-400"
                        >
                            {{ errors.credit_hours[0] }}
                        </div>
                    </div>
                </div>

                <!-- Right Column -->
                <div class="space-y-6">
                    <!-- Grade Level -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Grade Level
                        </label>
                        <select
                            v-model="formData.grade_level"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                            required
                        >
                            <option value="">Select Grade Level</option>
                            <option value="X">Grade X</option>
                            <option value="XI">Grade XI</option>
                            <option value="XII">Grade XII</option>
                            <option value="ALL">All Grades</option>
                        </select>
                        <div
                            v-if="errors.grade_level"
                            class="mt-1 text-sm text-red-600 dark:text-red-400"
                        >
                            {{ errors.grade_level[0] }}
                        </div>
                    </div>

                    <!-- Major -->
                    <div>
                        <SearchableSelect
                            v-model="formData.major_id"
                            :options="majorStore.activeMajors"
                            label="Major"
                            placeholder="Select Major"
                            optionLabel="name"
                            optionValue="id"
                        />
                        <div
                            v-if="errors.major_id"
                            class="mt-1 text-sm text-red-600 dark:text-red-400"
                        >
                            {{ errors.major_id[0] }}
                        </div>
                    </div>

                    <!-- Active Status -->
                    <div class="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-md">
                        <label
                            class="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                        >
                            <input
                                v-model="formData.is_active"
                                type="checkbox"
                                id="is_active"
                                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors mr-3"
                            />
                            <span> Active Status </span>
                        </label>
                        <p
                            class="text-xs text-gray-500 dark:text-gray-400 mt-2"
                        >
                            Enable this subject for class assignments
                        </p>
                    </div>
                </div>
            </div>
        </form>
    </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useSubjectStore } from "@/stores/subjectStore";
import { useMajorStore } from "@/stores/majorStore";
import { subjectApi } from "@/api/subject";
import SearchableSelect from "@/components/SearchableSelect.vue";
import BaseModal from "@/components/BaseModal.vue";

interface SubjectFormData {
    code: string;
    name: string;
    description?: string;
    credit_hours: number;
    grade_level: string;
    major_id?: string;
    is_active?: boolean;
}

// Props
const props = defineProps<{
    subject?: any;
    isEditing: boolean;
    showModal: boolean;
}>();

const emit = defineEmits<{
    close: [];
    save: [subjectData: any];
    saved: [];
}>();

const subjectStore = useSubjectStore();
const majorStore = useMajorStore();

// Form data
const formData = ref<SubjectFormData>({
    code: "",
    name: "",
    description: "",
    credit_hours: 2,
    grade_level: "",
    major_id: "",
    is_active: true,
});

const errors = ref<Record<string, string[]>>({});

// Watch for subject changes
watch(
    () => props.subject,
    (newSubject) => {
        if (newSubject) {
            errors.value = {};
            formData.value = {
                code: newSubject.code || "",
                name: newSubject.name || "",
                description: newSubject.description || "",
                credit_hours: newSubject.credit_hours || 2,
                grade_level: newSubject.grade_level || "",
                major_id: newSubject.major_id || "",
                is_active:
                    newSubject.is_active !== undefined
                        ? newSubject.is_active
                        : true,
            };
        }
    },
    { immediate: true },
);

// Load majors when modal opens
watch(
    () => props.showModal,
    (showModal) => {
        errors.value = {};
        if (!showModal) {
            formData.value = {
                code: "",
                name: "",
                description: "",
                credit_hours: 2,
                grade_level: "",
                major_id: "",
                is_active: true,
            };
        }
        if (showModal) {
            majorStore.fetchActiveMajors();
        }
    },
);

// Computed
const loading = computed(() => subjectStore.loading);

// Methods
const handleSubmit = async () => {
    try {
        // Convert code to uppercase before submitting
        if (formData.value.code) {
            formData.value.code = formData.value.code.toUpperCase();
        }

        // Ensure is_active is a boolean value
        const submitData = {
            ...formData.value,
            is_active: Boolean(formData.value.is_active),
        };

        if (props.isEditing && props.subject) {
            await subjectStore.updateSubject(props.subject.id, submitData);
        } else {
            await subjectStore.createSubject(submitData);
        }

        emit("saved");
    } catch (error: any) {
        console.error("Error saving subject:", error);

        // Handle validation errors from API response
        if (error.response?.status === 422) {
            errors.value = error.response.data.errors;
            return; // Do NOT throw upward for validation errors
        }
        // For unexpected errors, re-throw to let parent handle them
        throw error;
    }
};
</script>

<style scoped>
/* Form transitions */
input,
select,
textarea {
    transition:
        border-color 0.15s ease-in-out,
        box-shadow 0.15s ease-in-out,
        background-color 0.15s ease-in-out;
}

input:focus,
select:focus,
textarea:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Button transitions */
button {
    transition: all 0.2s ease-in-out;
}

/* Checkbox styling */
input[type="checkbox"]:checked {
    background-color: #3b82f6;
    border-color: #3b82f6;
}

/* Error states */
.border-red-500 {
    border-color: #ef4444;
}

.text-red-600 {
    color: #dc2626;
}

.dark .text-red-400 {
    color: #f87171;
}

/* Dark mode adjustments */
.dark input,
.dark select,
.dark textarea {
    background-color: #1f2937;
    border-color: #4b5563;
    color: #f9fafb;
}

.dark input:focus,
.dark select:focus,
.dark textarea:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Hover effects */
.hover\:bg-gray-50:hover {
    background-color: #f9fafb;
}

.dark .hover\:bg-gray-600:hover {
    background-color: #4b5563;
}

/* Focus ring styles */
.focus\:ring-blue-500:focus {
    --tw-ring-color: #3b82f6;
}

.focus\:ring-offset-2:focus {
    --tw-ring-offset-width: 2px;
}

/* Modal specific styles */
.max-w-4xl {
    max-width: 56rem;
}

/* Grid layout improvements */
.grid-cols-2 {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2rem;
}

/* Responsive design */
@media (max-width: 768px) {
    .grid-cols-2 {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
}
</style>
